import {
	type DraggableAttributes,
	type DraggableSyntheticListeners,
	useDroppable,
} from "@dnd-kit/core";
import {
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import type { CSSProperties } from "react";
import { useState } from "react";
import {
	AccordionItem,
	AccordionItemSize,
} from "@/components/Molecules/AccordionItem/AccordionItem";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";
import { useAdminFormEditorPaths } from "../../../hooks/use-admin-form-editor-paths";
import {
	formBuilderDropSectionFieldStripId,
	formBuilderDropSectionId,
} from "../../../lib/form-builder-drop-target";
import type { BuilderNode } from "../../../types";
import { useFormBuilderCanvas } from "../form-builder-canvas-context";
import { DragDotsIcon } from "../drag-dots-icon";
import {
	BUILDER_ROW_ACTIONS_VISIBILITY,
	FORM_BUILDER_ADD_STRIP_BASE,
} from "../form-builder-layout-classes";
import { BUILDER_CONTROL_FOCUS } from "../form-builder-shared-classes";
import { FormBuilderFieldRow } from "./form-builder-field-row";

/**
 * Sections at the root use `useSortable` here only. Root-level fields use
 * `FormBuilderFieldRow`'s sortable hook alone — a second `useSortable` on the parent
 * duplicated the same id and broke drops into nested section targets.
 */
export function FormBuilderRootSectionSortableRow({
	id,
	node,
	selectedId,
	onSelect,
	onMoveSection,
}: {
	id: string;
	node: Extract<BuilderNode, { kind: "section" }>;
	selectedId: string | null;
	onSelect: (id: string) => void;
	onMoveSection: (sectionId: string, dir: "up" | "down") => void;
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<FormBuilderSectionRootRow
			id={id}
			node={node}
			style={style}
			setNodeRef={setNodeRef}
			attributes={attributes}
			listeners={listeners}
			selectedId={selectedId}
			onSelect={onSelect}
			onMoveSection={onMoveSection}
		/>
	);
}

function FormBuilderSectionRootRow({
	id,
	node,
	style,
	setNodeRef,
	attributes,
	listeners,
	selectedId,
	onSelect,
	onMoveSection,
}: {
	id: string;
	node: Extract<BuilderNode, { kind: "section" }>;
	style: CSSProperties;
	setNodeRef: (node: HTMLElement | null) => void;
	attributes: DraggableAttributes;
	listeners: DraggableSyntheticListeners;
	selectedId: string | null;
	onSelect: (id: string) => void;
	onMoveSection: (sectionId: string, dir: "up" | "down") => void;
}) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	const { rootIds, clearSectionFields, deleteNode } = useFormBuilderCanvas();
	const { section, childFieldIds } = node;
	const sectionIndex = rootIds.indexOf(id);
	const canMoveSectionUp = sectionIndex > 0;
	const canMoveSectionDown =
		sectionIndex >= 0 && sectionIndex < rootIds.length - 1;
	const sectionHasFields = childFieldIds.length > 0;
	const [open, setOpen] = useState(section.defaultExpanded);
	const isSelected = selectedId === id;

	const sectionFieldDropLabelIdle = t(
		paths.formEditor.builder.dropFieldInSection,
	);

	const sectionFieldDropLabelDragOver = t(
		paths.formEditor.builder.dropFieldInSectionDragOver,
	);

	return (
		<li ref={setNodeRef} style={style} className="group relative list-none">
			<div
				className={cx(
					"absolute top-3 right-14 z-10 flex gap-1",
					BUILDER_ROW_ACTIONS_VISIBILITY,
				)}
			>
				<button
					type="button"
					disabled={!canMoveSectionUp}
					className={cx(
						"rounded p-1 text-text-tertiary",
						BUILDER_CONTROL_FOCUS,
						canMoveSectionUp
							? "cursor-pointer hover:bg-background-tertiary"
							: "cursor-not-allowed opacity-40",
					)}
					onClick={() => {
						if (!canMoveSectionUp) return;
						onMoveSection(id, "up");
					}}
					aria-label={t(paths.formEditor.builder.moveUp)}
				>
					<ArrowUp className="size-4" aria-hidden />
				</button>
				<button
					type="button"
					disabled={!canMoveSectionDown}
					className={cx(
						"rounded p-1 text-text-tertiary",
						BUILDER_CONTROL_FOCUS,
						canMoveSectionDown
							? "cursor-pointer hover:bg-background-tertiary"
							: "cursor-not-allowed opacity-40",
					)}
					onClick={() => {
						if (!canMoveSectionDown) return;
						onMoveSection(id, "down");
					}}
					aria-label={t(paths.formEditor.builder.moveDown)}
				>
					<ArrowDown className="size-4" aria-hidden />
				</button>
				<button
					type="button"
					className={cx(
						"cursor-pointer rounded p-1 text-text-tertiary transition-colors",
						BUILDER_CONTROL_FOCUS,
						"hover:bg-error-50 hover:text-error-600",
					)}
					aria-label={
						sectionHasFields
							? t(paths.formEditor.builder.clearSectionFields)
							: t(paths.formEditor.builder.deleteEmptySection)
					}
					onClick={(e) => {
						e.stopPropagation();
						if (sectionHasFields) {
							clearSectionFields(id);
							return;
						}
						deleteNode(id);
					}}
				>
					<Trash2 className="size-4" aria-hidden />
				</button>
			</div>
			<AccordionItem
				className={cx("w-full", isSelected && "ring-2 ring-brand")}
				size={AccordionItemSize.Sm}
				primaryText={section.label}
				secondaryText={t(paths.formEditor.builder.sectionFieldCount, {
					count: childFieldIds.length,
				})}
				showBadge={false}
				showLeadingSlot
				leadingSlot={
					<span
						className="cursor-grab active:cursor-grabbing"
						aria-label={t(paths.formEditor.a11y.dragHandleSection, {
							name: section.label,
						})}
						{...attributes}
						{...listeners}
						onPointerDown={(event) => {
							listeners?.onPointerDown?.(event);
							event.stopPropagation();
						}}
						onClick={(event) => {
							event.preventDefault();
							event.stopPropagation();
						}}
					>
						<DragDotsIcon className="size-5" />
					</span>
				}
				isExpanded={section.collapsible ? open : true}
				onExpandedChange={(next) => {
					onSelect(id);
					if (section.collapsible) setOpen(next);
				}}
			>
				<SectionDropZone sectionId={id}>
					{section.helpText ? (
						<p className="mb-2 text-xs text-fg-quaternary">{section.helpText}</p>
					) : null}
					<SortableContext
						items={childFieldIds}
						strategy={verticalListSortingStrategy}
					>
						<ul
							className="flex list-none flex-col gap-2"
							aria-label={t(paths.formEditor.a11y.sectionFieldsList, {
								name: section.label,
							})}
						>
							{childFieldIds.map((fid) => (
								<FormBuilderFieldRow
									key={fid}
									id={fid}
									selectedId={selectedId}
									onSelect={onSelect}
								/>
							))}
							<li className="list-none">
								<SectionFieldDropStrip
									sectionId={id}
									idleLabel={sectionFieldDropLabelIdle}
									dragOverLabel={sectionFieldDropLabelDragOver}
									ariaLabel={t(paths.formEditor.a11y.sectionAppendDrop, {
										name: section.label,
									})}
								/>
							</li>
						</ul>
					</SortableContext>
				</SectionDropZone>
			</AccordionItem>
		</li>
	);
}

function SectionDropZone({
	sectionId,
	children,
}: {
	sectionId: string;
	children: React.ReactNode;
}) {
	const { setNodeRef } = useDroppable({
		id: formBuilderDropSectionId(sectionId),
	});
	return (
		<div ref={setNodeRef} className="min-h-0">
			{children}
		</div>
	);
}

/**
 * Append-field strip with its own droppable id so `isOver` matches drag position (not the whole section).
 */
function SectionFieldDropStrip({
	sectionId,
	idleLabel,
	dragOverLabel,
	ariaLabel,
}: {
	sectionId: string;
	idleLabel: string;
	dragOverLabel: string;
	ariaLabel: string;
}) {
	const { setNodeRef, isOver } = useDroppable({
		id: formBuilderDropSectionFieldStripId(sectionId),
	});

	const label = isOver ? dragOverLabel : idleLabel;

	return (
		<section
			ref={setNodeRef}
			aria-label={ariaLabel}
			className={cx(
				FORM_BUILDER_ADD_STRIP_BASE,
				isOver && "border-brand-500 bg-brand-50",
			)}
		>
			<span>{label}</span>
		</section>
	);
}
